import axios from "axios"
import { ComposerPromptValidationIssue } from "modules/shared/components/ComposerPromptValidationIssues"
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react"
import { DefaultValues, UseFormReturn } from "react-hook-form"
import {
  BigBlueBackorderConfigSchema,
  BigBlueCampaignTriggerEnum,
  UpdateCampaignSchema,
} from "schema"
import { z } from "zod"
import { UpdateCampaignSchemaFrontend } from "./useCampaignForm"
import { useUpdateCampaignMutation } from "./useUpdateCampaignMutation"

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type Payload = z.input<typeof UpdateCampaignSchema>
type ComposerPromptField = "description" | "toneOfVoice"

const DEBOUNCE_MS = 500
const COMPOSER_PROMPT_FIELDS = ["description", "toneOfVoice"] as const

function transformForPayload<K extends keyof FormValues>(
  key: K,
  value: FormValues[K],
): Payload[keyof Payload] {
  if (key === "templates") {
    return (value as FormValues["templates"])?.map((t) => t.id) as Payload[keyof Payload]
  }
  if (key === "openingMessages") {
    return (value as FormValues["openingMessages"])?.map((m) => m.id) as Payload[keyof Payload]
  }
  if (key === "goals") {
    return (value as FormValues["goals"])?.map((g) =>
      "id" in g ? { id: g.id } : g,
    ) as Payload[keyof Payload]
  }
  return value as Payload[keyof Payload]
}

// Backend services (updateCampaignIntegration, updateCampaignTemplates,
// updateCampaignOpeningMessages) branch on `channel`, so it must be in the payload
// whenever any channel-bound field is being saved.
const CHANNEL_BOUND_KEYS = new Set<keyof FormValues>([
  "whatsappNumberId",
  "templates",
  "openingMessages",
])

// undefined / null / empty array all collapse to "no value" so the diff
// doesn't churn on equivalent representations of an absent value.
function normalize(value: unknown): unknown {
  if (value === undefined || value === null) return null
  if (Array.isArray(value) && value.length === 0) return null
  return value
}

function buildDiff(current: Partial<FormValues>, lastSaved: Partial<FormValues>): Partial<Payload> {
  const payload: Record<string, unknown> = {}
  const keys = new Set([...Object.keys(current), ...Object.keys(lastSaved)]) as Set<
    keyof FormValues
  >
  let hasChannelBoundChange = false
  for (const key of keys) {
    if (JSON.stringify(normalize(current[key])) !== JSON.stringify(normalize(lastSaved[key]))) {
      payload[key] = transformForPayload(key, current[key] as FormValues[typeof key])
      if (CHANNEL_BOUND_KEYS.has(key)) hasChannelBoundChange = true
    }
  }
  if (hasChannelBoundChange && payload.channel === undefined) {
    payload.channel = current.channel ?? null
  }
  return payload as Partial<Payload>
}

function hasInvalidBackorderConfig(triggerIntegration: FormValues["triggerIntegration"]) {
  if (
    triggerIntegration?.type !== "bigblue" ||
    triggerIntegration.trigger !== BigBlueCampaignTriggerEnum.Backorder ||
    triggerIntegration.backorder === undefined
  ) {
    return false
  }

  return !BigBlueBackorderConfigSchema.safeParse(triggerIntegration.backorder).success
}

type Options = {
  autoSave?: boolean
  resetDirtyOnSave?: boolean
}

// RHF's getValues() returns a reference to its internal state; subsequent
// setValue calls would mutate that same object, invalidating any "last saved"
// snapshot we hold. Always deep-clone before storing.
function snapshotValues(form: UseFormReturn<FormValues>): Partial<FormValues> {
  return structuredClone(form.getValues()) as Partial<FormValues>
}

export function useCampaignSave(
  form: UseFormReturn<FormValues>,
  campaignId: string,
  { autoSave = true, resetDirtyOnSave = false }: Options = {},
) {
  const { mutateAsync: updateCampaign } = useUpdateCampaignMutation(campaignId)
  const [issuesByField, setIssuesByField] = useState<
    Record<string, ComposerPromptValidationIssue[]>
  >({})
  const [savingFields, setSavingFields] = useState<string[]>([])
  const isValidationReviewOpenRef = useRef(false)
  const isComposerPromptEditingRef = useRef(false)

  const lastSavedRef = useRef<Partial<FormValues>>(snapshotValues(form))
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pendingSaveRef = useRef<Promise<void> | null>(null)

  // Runs sequential save attempts until form values match lastSavedRef.
  // Loops so that changes made during an in-flight save get persisted on the
  // next iteration without queuing a separate run.
  const runSaveRef = useRef<(field?: ComposerPromptField) => Promise<void>>(() => Promise.resolve())
  runSaveRef.current = async (field?: ComposerPromptField) => {
    while (true) {
      const snapshot = snapshotValues(form)
      const fullPayload = buildDiff(snapshot, lastSavedRef.current)
      let payload = fullPayload
      const isInvalidBackorderConfig = hasInvalidBackorderConfig(snapshot.triggerIntegration)
      if (isInvalidBackorderConfig) {
        payload = { ...fullPayload }
        delete payload.triggerIntegration
      }
      if (field) {
        payload = field in fullPayload ? { [field]: fullPayload[field] } : {}
      }
      if (Object.keys(payload).length === 0) return
      const changedComposerFields = COMPOSER_PROMPT_FIELDS.filter((key) => key in payload)
      setSavingFields(Object.keys(payload))
      try {
        await updateCampaign(payload)
        if (field) {
          lastSavedRef.current = { ...lastSavedRef.current, [field]: snapshot[field] }
        } else if (isInvalidBackorderConfig) {
          lastSavedRef.current = {
            ...snapshot,
            triggerIntegration: lastSavedRef.current.triggerIntegration,
          }
        } else {
          lastSavedRef.current = snapshot
        }
        if (changedComposerFields.length > 0) {
          setIssuesByField((issues) => {
            const nextIssues = { ...issues }
            for (const key of changedComposerFields) nextIssues[key] = []
            return nextIssues
          })
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          const issues = error.response.data?.error?.issues as
            ComposerPromptValidationIssue[] | undefined
          if (issues) {
            const groupedIssues = issues.reduce<Record<string, ComposerPromptValidationIssue[]>>(
              (grouped, issue) => {
                ;(grouped[issue.field] ??= []).push(issue)
                return grouped
              },
              {},
            )
            setIssuesByField((currentIssues) => {
              const nextIssues = { ...currentIssues }
              for (const key of changedComposerFields) nextIssues[key] = groupedIssues[key] ?? []
              return nextIssues
            })
          }
        }
        throw error
      } finally {
        setSavingFields([])
      }
      if (resetDirtyOnSave) form.reset(snapshot)
      if (field) return
    }
  }

  const flush = useCallback((): Promise<void> => {
    if (isValidationReviewOpenRef.current || isComposerPromptEditingRef.current) {
      return Promise.resolve()
    }
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = undefined
    }
    // Coalesce concurrent callers onto the in-flight save promise.
    if (pendingSaveRef.current) return pendingSaveRef.current
    const promise = runSaveRef.current().finally(() => {
      // Always clear the slot — including on rejection — so the next call
      // starts a fresh save attempt against the (unchanged) lastSavedRef.
      pendingSaveRef.current = null
    })
    pendingSaveRef.current = promise
    return promise
  }, [])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") flush()
    }
    const handleFlush = () => {
      isComposerPromptEditingRef.current = false
      flush()
    }
    document.addEventListener("visibilitychange", handleVisibility)
    window.addEventListener("pagehide", handleFlush)
    window.addEventListener("beforeunload", handleFlush)

    let subscription: { unsubscribe: () => void } | undefined
    if (autoSave) {
      subscription = form.watch((_values, { name: _name }) => {
        if (isValidationReviewOpenRef.current || isComposerPromptEditingRef.current) return
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = setTimeout(() => {
          // Swallow auto-save errors so the debounce loop doesn't see an
          // unhandled rejection; manual flush() callers still receive errors.
          flush().catch(() => {})
        }, DEBOUNCE_MS)
      })
    }

    return () => {
      subscription?.unsubscribe()
      clearTimeout(saveTimeoutRef.current)
      document.removeEventListener("visibilitychange", handleVisibility)
      window.removeEventListener("pagehide", handleFlush)
      window.removeEventListener("beforeunload", handleFlush)
      flush()
    }
  }, [form, flush, autoSave])

  const setValidationReviewOpen = useCallback(
    (open: boolean) => {
      isValidationReviewOpenRef.current = open
      if (!open) void flush().catch(() => {})
    },
    [flush],
  )

  const setComposerPromptEditing = useCallback(
    (editing: boolean, field?: ComposerPromptField) => {
      isComposerPromptEditingRef.current = editing
      if (!editing) {
        const save = field ? runSaveRef.current(field).then(flush) : flush()
        void save.catch(() => {})
      }
    },
    [flush],
  )

  const syncFromCampaign = useCallback(
    (nextDefaults: DefaultValues<FormValues>) => {
      const current = form.getValues()
      const hasUnsaved = Object.keys(buildDiff(current, lastSavedRef.current)).length > 0
      if (hasUnsaved || form.formState.isDirty) return
      form.reset(nextDefaults)
      // Snapshot after reset so lastSaved reflects what RHF actually stored
      // (RHF may normalize values, e.g. arrays/nullish); prevents false-positive diffs
      // on the next watch fire triggered by the reset itself.
      lastSavedRef.current = snapshotValues(form)
    },
    [form],
  )

  return {
    flush,
    syncFromCampaign,
    issuesByField,
    savingFields,
    setValidationReviewOpen,
    setComposerPromptEditing,
  }
}

type CampaignSaveContextValue = {
  flush: () => Promise<void>
  issuesByField: Record<string, ComposerPromptValidationIssue[]>
  savingFields: string[]
  setValidationReviewOpen: (open: boolean) => void
  setComposerPromptEditing: (editing: boolean, field?: ComposerPromptField) => void
}

const CampaignSaveContext = createContext<CampaignSaveContextValue | undefined>(undefined)

export const CampaignSaveProvider = CampaignSaveContext.Provider

export function useCampaignSaveContext() {
  const ctx = useContext(CampaignSaveContext)
  if (!ctx) throw new Error("useCampaignSaveContext must be used within CampaignSaveProvider")
  return ctx
}
